import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { useRecoilState } from 'recoil';
import projectAtom from 'ProjectManager/Project/Common/recoil/project/projectAtom';
import axios from 'axios';
import notify from 'Common/utils/notify';
import { toast } from 'react-toastify';
import dataUriToBlob from 'Common/utils/dataUriToBlob';
import createNewDate from 'Common/utils/createNewDate';
import formatDate from 'Common/utils/formatDate';
import getProject from 'ProjectManager/Project/Common/api/getProject';
import { pick } from 'lodash';
import useWindowSize from '@react-hook/window-size';
import { isMobile } from 'react-device-detect';
import TurnYourDeviceInLandscapeModeOverlay from 'Common/components/TurnYourDeviceInLandscapeModeOverlay';
import Modal from 'Common/components/Modals/Modal';
import Button from 'Common/components/Button';
import ModalTitle from 'Common/components/Modals/ModalTitle';
import FormField from 'Common/components/Form/FormField';
import Input from 'Common/components/Form/Fields/Input';
import SignaturePad from 'Common/components/SignaturePad';
import confirmEmployeeParticipationInIntroduction
    from 'ProjectManager/Project/Introductions/Common/api/confirmEmployeeParticipationInIntroduction';

const FixedWrapper = styled.div`
    > div {
        position: fixed;
        z-index: 10000;
    }
`;

const ConfirmationModal = ({ employee, introduction, onClose }) => {
    const [{ id: projectId }, setProject] = useRecoilState(projectAtom);

    const [isConfirming, setIsConfirming] = useState(false);
    const [password, setPassword] = useState('');

    const handlePasswordChange = e => {
        setPassword(e.target.value);
    };

    const signaturePad = useRef();

    const user = employee.user;

    const userId = employee.user.id;

    const isAutomaticSigningAllowed = user.isAutomaticSigningAllowed;

    const source = useMemo(() => axios.CancelToken.source(), []);

    useEffect(
        () => () => {
            source.cancel();
        },
        [source],
    );

    const handleConfirm = async () => {
        if (!isAutomaticSigningAllowed && signaturePad.current.isEmpty()) {
            notify('Das Unterschriftsfeld darf nicht leer sein', {
                type: toast.TYPE.ERROR,
            });
        } else {
            setIsConfirming(true);

            try {
                let signatureFile = null;

                if (!isAutomaticSigningAllowed) {
                    const blob = dataUriToBlob(
                        signaturePad.current.toDataURL(),
                    );
                    const date = createNewDate();

                    signatureFile = new File(
                        [blob],
                        `ConfirmationSignature_Participant_${userId}_${formatDate(
                            date,
                            'yyyy-MM-dd_H-mm-ss',
                        )}.png`,
                        {
                            type: 'image/png',
                            lastModified: date,
                        },
                    );
                }

                await confirmEmployeeParticipationInIntroduction(
                    projectId,
                    introduction.id,
                    employee.id,
                    password,
                    signatureFile,
                    source.token,
                );

                const projectResponse = await getProject(
                    projectId,
                    [
                        'statistics',
                        'introductions',
                        'introductions.supervisor',
                        'introductions.participants',
                        'introductions.participants.projectParticipant',
                        'introductions.participants.projectParticipant.employee',
                        'introductions.participants.projectParticipant.employee.user',
                    ],
                    source.token,
                );

                setProject(prevProject => ({
                    ...prevProject,
                    ...pick(projectResponse.data, [
                        'statistics',
                        'introductions',
                    ]),
                }));

                notify('Bestätigung war erfolgreich', {
                    type: toast.TYPE.SUCCESS,
                });

                onClose();
            } catch (error) {
                if (!axios.isCancel(error)) {
                    if (error.response.status === 400) {
                        notify('Bitte Passworteingabe überprüfen', {
                            type: toast.TYPE.ERROR,
                        });
                    } else {
                        notify(
                            'Ein Fehler ist aufgetreten. Bitte versuche es erneut.',
                            {
                                type: toast.TYPE.ERROR,
                            },
                        );
                    }
                }
            }

            setIsConfirming(false);
        }
    };

    const [width, height] = useWindowSize();
    const isPortrait = width < height;
    const isOverlayVisible =
        isMobile && isPortrait && !isAutomaticSigningAllowed;

    if (isOverlayVisible) {
        return (
            <FixedWrapper>
                <TurnYourDeviceInLandscapeModeOverlay />
            </FixedWrapper>
        );
    }

    return (
        <Modal
            isOpen
            onRequestClose={isConfirming ? undefined : onClose}
            noTransition
            buttons={
                <>
                    <Button
                        text
                        dark
                        type="button"
                        onClick={onClose}
                        disabled={isConfirming}
                    >
                        Abbrechen
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        isLoading={isConfirming}
                        disabled={isConfirming}
                    >
                        Bestätigen
                    </Button>
                </>
            }
        >
            <ModalTitle>Bestätigung</ModalTitle>
            <p>
                {`${employee.lastName}, ${employee.firstName}`}
            </p>
            <FormField>
                <Input
                    type="password"
                    label="Passwort"
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="off"
                />
            </FormField>
            {!isAutomaticSigningAllowed && (
                <SignaturePad ref={signaturePad} light />
            )}
        </Modal>
    );
};

ConfirmationModal.propTypes = {
    employee: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ConfirmationModal;
